import React, { useState, useEffect } from 'react';
import TabNavigation from './components/TabNavigation';
import RoutesTab from './components/tabs/RoutesTab';
import TasksTab from './components/tabs/TasksTab';
import LeadsTab from './components/tabs/LeadsTab';
import TrackingTab from './components/tabs/TrackingTab';
import { initDatabase } from '../database/db';

/**
 * Main Application Component
 * GreenTech Shredding Route Planner
 *
 * Multi-tab field service management application with:
 * - Routes: Route planning and optimization with save/load
 * - Tasks: Task management with Perfex CRM integration
 * - Leads Follow-ups: Lead management with call scripts and custom fields
 * - Tracking: Placeholder for future tracking features
 */
function App() {
    const [activeTab, setActiveTab] = useState('routes');
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize application
    useEffect(() => {
        initializeApp();
    }, []);

    const initializeApp = async () => {
        try {
            // Initialize SQLite database
            await initDatabase();

            // Get default tab from config if available
            if (typeof window !== 'undefined' && window.CONFIG?.APP?.DEFAULT_TAB) {
                setActiveTab(window.CONFIG.APP.DEFAULT_TAB);
            }

            setIsInitialized(true);
        } catch (error) {
            console.error('Error initializing app:', error);
            // Continue anyway, some features may not work
            setIsInitialized(true);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);

        // Save to localStorage for persistence
        try {
            localStorage.setItem('greentech_active_tab', tabId);
        } catch (error) {
            console.error('Error saving active tab:', error);
        }
    };

    // Render active tab content
    const renderTabContent = () => {
        switch (activeTab) {
            case 'routes':
                return <RoutesTab />;
            case 'tasks':
                return <TasksTab />;
            case 'leads':
                return <LeadsTab />;
            case 'tracking':
                return <TrackingTab />;
            default:
                return <RoutesTab />;
        }
    };

    // Show loading screen while initializing
    if (!isInitialized) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 to-blue-50">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full mb-4 animate-pulse">
                        <svg
                            className="w-8 h-8 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        GreenTech Shredding
                    </h2>
                    <p className="text-gray-600">Loading application...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col bg-gray-50">
            {/* Top Navigation */}
            <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />

            {/* Tab Content */}
            <div className="flex-1 overflow-hidden">
                {renderTabContent()}
            </div>
        </div>
    );
}

export default App;
